import type { NotificationSoundId } from './notificationSound';

export type ShellType =
  | 'pwsh' | 'powershell' | 'gitbash' | 'cmd' | 'wsl' // Windows
  | 'bash' | 'zsh' | 'fish' | 'sh'; // Unix (Linux/macOS)

type ShellOs = 'windows' | 'unix';

export const SHELL_CONFIGS: Record<ShellType, { name: string; command: string | null; os: ShellOs }> = {
  // Windows shells
  pwsh: { name: 'PowerShell 7', command: 'pwsh', os: 'windows' },
  powershell: { name: 'Windows PowerShell', command: null, os: 'windows' }, // null = use default (legacy 5.1)
  gitbash: { name: 'Git Bash', command: 'bash', os: 'windows' },
  cmd: { name: 'CMD', command: 'cmd', os: 'windows' },
  wsl: { name: 'WSL', command: 'wsl', os: 'windows' },
  // Unix shells (Linux / macOS)
  bash: { name: 'Bash', command: 'bash', os: 'unix' },
  zsh: { name: 'Zsh', command: 'zsh', os: 'unix' },
  fish: { name: 'Fish', command: 'fish', os: 'unix' },
  sh: { name: 'sh', command: 'sh', os: 'unix' },
};

/** True when running on Windows. `navigator.platform` is deprecated but is
 *  the only synchronous signal available at module load (the Tauri os plugin
 *  is async); it's reliable enough to pick the right shell family. */
export const IS_WINDOWS =
  typeof navigator !== 'undefined' && /win/i.test(navigator.platform);

/** Default shell for the current platform — used when a project has no saved
 *  preference. On Unix the backend ultimately resolves to `$SHELL`, but the UI
 *  needs a concrete selection that exists in this platform's menu. */
export const DEFAULT_SHELL: ShellType = IS_WINDOWS ? 'pwsh' : 'bash';

/** Shell menu entries that apply to the current OS — so Linux/macOS users
 *  don't see Windows-only shells (CMD/WSL/PowerShell) and vice versa. */
export function availableShellEntries(): [ShellType, (typeof SHELL_CONFIGS)[ShellType]][] {
  const family: ShellOs = IS_WINDOWS ? 'windows' : 'unix';
  return (Object.entries(SHELL_CONFIGS) as [ShellType, (typeof SHELL_CONFIGS)[ShellType]][])
    .filter(([, cfg]) => cfg.os === family);
}

export interface ProjectTerminalSettings {
  autoLaunchAgent: boolean;
  agentFlags: string[];
  /**
   * Free-form launch flags appended verbatim after the predefined toggles.
   * Lets users pass options the UI doesn't model (e.g. `--variant high`,
   * `--cd /tmp/foo`). Empty string = nothing appended.
   */
  customAgentFlags: string;
  /**
   * Per-project preferred model id (e.g. `anthropic/claude-sonnet-4-5`,
   * `opencode/big-pickle`). When set, gets appended to the launch command
   * as `<config.modelFlag> <preferredModel>` for agents whose CLI accepts a
   * model flag. Falls back to the global default in settings.defaultAgentModels
   * when undefined. Empty string = explicit "use the agent's own default".
   */
  preferredModel?: string;
  notificationSound: NotificationSoundId;
  defaultShell: ShellType;
  /**
   * Whether this project has already launched its agent at least once. Set to
   * `false` by the wizard on a brand-new project; the first auto-launch runs a
   * FRESH session (no --continue) and then flips this to `true`. `undefined`
   * (projects created before this existed) is treated as "already launched", so
   * their behavior is unchanged.
   */
  agentHasLaunched?: boolean;
}

export function getProjectTerminalSettings(settingsJson: string | null): ProjectTerminalSettings {
  const defaults: ProjectTerminalSettings = { autoLaunchAgent: true, agentFlags: [], customAgentFlags: '', notificationSound: 'chime', defaultShell: DEFAULT_SHELL };
  if (!settingsJson) return defaults;
  try {
    const parsed = JSON.parse(settingsJson);
    return {
      autoLaunchAgent: parsed?.terminal?.autoLaunchAgent ?? true,
      agentFlags: parsed?.terminal?.agentFlags ?? [],
      customAgentFlags: parsed?.terminal?.customAgentFlags ?? '',
      preferredModel: parsed?.terminal?.preferredModel,
      notificationSound: parsed?.terminal?.notificationSound ?? 'chime',
      defaultShell: parsed?.terminal?.defaultShell ?? DEFAULT_SHELL,
      agentHasLaunched: parsed?.terminal?.agentHasLaunched,
    };
  } catch {
    return defaults;
  }
}

export function setProjectTerminalSettings(
  currentJson: string | null,
  terminal: Partial<ProjectTerminalSettings>,
): string {
  let current: Record<string, unknown> = {};
  if (currentJson) {
    try { current = JSON.parse(currentJson); } catch { /* use empty */ }
  }
  current.terminal = { ...((current.terminal as object) ?? {}), ...terminal };
  return JSON.stringify(current);
}

// --- Team / group membership (per-project) ---
//
// A project's "Team" (group) assignment rides in the same `settings` JSON blob
// as terminal/browser/runner settings, under the `group` namespace. Storing it
// here (rather than a dedicated project column) means reassigning a project is
// one `updateProject({ settings })` call with no schema change, and the
// assignment syncs to remote clients with the project row. The group
// DEFINITIONS (names/colors) live in the core DB (groupStore / group.* IPC);
// this only records WHICH group a project belongs to.

/** The group id this project belongs to, or null when unassigned ("Ungrouped"). */
export function getProjectGroupId(settingsJson: string | null): string | null {
  if (!settingsJson) return null;
  try {
    const parsed = JSON.parse(settingsJson) as { group?: { id?: string | null } };
    return parsed?.group?.id ?? null;
  } catch {
    return null;
  }
}

/** Return an updated settings JSON with the project's group set (null to clear). */
export function setProjectGroupId(currentJson: string | null, groupId: string | null): string {
  let current: Record<string, unknown> = {};
  if (currentJson) {
    try { current = JSON.parse(currentJson); } catch { /* use empty */ }
  }
  current.group = { id: groupId };
  return JSON.stringify(current);
}

// --- Browser tab (per-project) ---

export interface BrowserBookmark {
  /** Stable id (uuid-ish) so React keys don't collide on rename. */
  id: string;
  /** Display label on the bookmarks bar — kept short, ~20 chars looks best. */
  name: string;
  url: string;
}

export interface BrowserTabState {
  /** Stable id used as part of the Tauri webview label, so it must be
   *  alphanum/dash only (validated in commands_browser.rs). */
  id: string;
  url: string;
  /** Display title — falls back to the URL host when blank. */
  title: string;
}

export interface ProjectBrowserSettings {
  /** @deprecated Migrated into `tabs` on first load. Kept for back-compat
   *  when reading older project records. */
  lastUrl: string;
  /** When true, the BrowserTab automatically navigates to a localhost
   *  URL detected in terminal output (Vite "Local: http://..." style).
   *  Default true — that's the killer-feature workflow. */
  autoOpenDetected: boolean;
  /** Per-project bookmarks shown in the BrowserTab bookmarks bar.
   *  Lives on the project so different projects can pin different
   *  endpoints (admin UIs, dashboards, third-party tools). */
  bookmarks: BrowserBookmark[];
  /** Open tabs within the BrowserTab pane. Each entry gets its own
   *  Tauri child webview. Empty array = "no tabs yet" UI. */
  tabs: BrowserTabState[];
  /** ID of the currently-focused tab. Falls back to tabs[0]?.id on read. */
  activeTabId: string | null;
}

const BROWSER_DEFAULTS: ProjectBrowserSettings = {
  lastUrl: '',
  autoOpenDetected: true,
  bookmarks: [],
  tabs: [],
  activeTabId: null,
};

export function getProjectBrowserSettings(settingsJson: string | null): ProjectBrowserSettings {
  if (!settingsJson) return { ...BROWSER_DEFAULTS, bookmarks: [], tabs: [] };
  try {
    const parsed = JSON.parse(settingsJson) as { browser?: Partial<ProjectBrowserSettings> };
    return {
      lastUrl: parsed?.browser?.lastUrl ?? BROWSER_DEFAULTS.lastUrl,
      autoOpenDetected: parsed?.browser?.autoOpenDetected ?? BROWSER_DEFAULTS.autoOpenDetected,
      bookmarks: Array.isArray(parsed?.browser?.bookmarks) ? parsed.browser.bookmarks as BrowserBookmark[] : [],
      tabs: Array.isArray(parsed?.browser?.tabs) ? parsed.browser.tabs as BrowserTabState[] : [],
      activeTabId: parsed?.browser?.activeTabId ?? null,
    };
  } catch {
    return { ...BROWSER_DEFAULTS, bookmarks: [], tabs: [] };
  }
}

export function setProjectBrowserSettings(
  currentJson: string | null,
  browser: Partial<ProjectBrowserSettings>,
): string {
  let current: Record<string, unknown> = {};
  if (currentJson) {
    try { current = JSON.parse(currentJson); } catch { /* use empty */ }
  }
  current.browser = { ...((current.browser as object) ?? {}), ...browser };
  return JSON.stringify(current);
}

// --- Run Configurations ---

export interface RunConfiguration {
  id: string;
  name: string;
  command: string;
  /** Shell to run this config in. Omit or 'default' to use the project's defaultShell. */
  shell?: ShellType | 'default';
}

// ─── .ateliercode/ file-based config types ───

/** Schema for .ateliercode/config.json */
export interface AteliercodeConfig {
  $schema?: string;
  agent?: {
    type?: string;
    autoLaunch?: boolean;
    flags?: string[];
    /** Free-form launch flags appended verbatim after `flags`. */
    customFlags?: string;
  };
  terminal?: {
    defaultShell?: ShellType;
    notificationSound?: string;
  };
  /** Autonomous ticket-queue system. Each role can be played by any
   *  agent registered in `work.agents`; the orchestration scripts
   *  (scripts/queue/*) resolve role → agent at invoke time, so changing
   *  who plays a role is a config edit, not a code change. */
  work?: WorkQueueConfig;
  /**
   * Name of the Claude identity this project uses (matches an identity defined
   * in app Settings → Claude Identities). Selects which CLAUDE_CONFIG_DIR the
   * project's terminals/agent run under. Omit to use the global default identity.
   */
  claudeIdentity?: string;
  /**
   * Name of the Copilot identity this project uses (matches an identity defined
   * in app Settings → Copilot Identities). Selects which COPILOT_HOME the
   * project's terminals/Copilot CLI run under. Omit to use the global default.
   */
  copilotIdentity?: string;
}

/** Roles in the ticket-queue loop. Each maps to one entry in
 *  `WorkQueueConfig.agents` so the control scripts know what CLI to
 *  invoke for that step. */
export type QueueRole = 'planner' | 'worker' | 'validator' | 'tester';

export interface WorkQueueConfig {
  /** Where the queue folders live, relative to the project root.
   *  Default: `.ateliercode/work`. Configurable so a project that
   *  doesn't want the queue under .ateliercode/ can point elsewhere. */
  path: string;
  /** Maps each role to the agent id that should play it. The agent id
   *  must exist in `agents`. */
  roles: Record<QueueRole, string>;
  /** Registry of agents available to play roles. Key is a free-form id
   *  (e.g. `claude-code`, `opencode-local`); value tells the control
   *  scripts how to spawn the agent's CLI. */
  agents: Record<string, AgentRegistryEntry>;
  /** Max failed verification attempts before a ticket goes to escalate/.
   *  Default 3 — the circuit breaker that prevents infinite retry loops. */
  maxAttempts?: number;
  /** Number of concurrent local workers the auto-loop may run. Default 3.
   *  Dial down when running multiple projects so they share machine capacity
   *  (each worker is a full local-model inference). Range 1–16. */
  maxConcurrent?: number;
}

export interface AgentRegistryEntry {
  /** Display name shown in the Settings UI dropdowns. */
  displayName: string;
  /** Binary to invoke (e.g. `claude`, `opencode`, `codex`, `gemini`).
   *  Must be on $PATH or specified as an absolute path. */
  command: string;
  /** Args prepended on every invocation (e.g. `["--model", "qwen-primary"]`). */
  args?: string[];
  /** Optional env vars merged into the spawn (e.g. local-model endpoint). */
  env?: Record<string, string>;
}

export const DEFAULT_WORK_QUEUE: WorkQueueConfig = {
  path: '.ateliercode/work',
  roles: {
    planner: 'claude-code',
    worker: 'claude-code',
    validator: 'claude-code',
    tester: 'claude-code',
  },
  agents: {
    'claude-code': {
      displayName: 'Claude Code',
      command: 'claude',
    },
    'opencode-local': {
      displayName: 'OpenCode (local vLLM)',
      command: 'opencode',
      args: ['--model', 'qwen-primary'],
      env: { OPENAI_BASE_URL: 'https://llm.unveiledsoftwaresolutions.com/v1' },
    },
    codex: {
      displayName: 'Codex',
      command: 'codex',
    },
    gemini: {
      displayName: 'Gemini',
      command: 'gemini',
    },
  },
  maxAttempts: 3,
  maxConcurrent: 3,
};

export function getWorkQueueConfig(config: AteliercodeConfig | null): WorkQueueConfig {
  const w = config?.work;
  if (!w) return DEFAULT_WORK_QUEUE;
  return {
    path: w.path || DEFAULT_WORK_QUEUE.path,
    roles: { ...DEFAULT_WORK_QUEUE.roles, ...(w.roles ?? {}) } as Record<QueueRole, string>,
    agents: { ...DEFAULT_WORK_QUEUE.agents, ...(w.agents ?? {}) },
    maxAttempts: w.maxAttempts ?? DEFAULT_WORK_QUEUE.maxAttempts,
    maxConcurrent: w.maxConcurrent ?? DEFAULT_WORK_QUEUE.maxConcurrent,
  };
}

/** Schema for .ateliercode/runners.json */
export interface AteliercodeRunners {
  $schema?: string;
  configs: Array<{
    name: string;
    command: string;
    shell?: ShellType | 'default';
  }>;
  default?: string | null;
}

export interface RunHistoryEntry {
  name: string;
  command: string;
  lastRun: number; // timestamp
}

export interface ProjectRunnerSettings {
  configs: RunConfiguration[];
  defaultConfigId: string | null;
  history: RunHistoryEntry[];
}

export function getProjectRunnerSettings(settingsJson: string | null): ProjectRunnerSettings {
  const defaults: ProjectRunnerSettings = { configs: [], defaultConfigId: null, history: [] };
  if (!settingsJson) return defaults;
  try {
    const parsed = JSON.parse(settingsJson);
    return {
      configs: parsed?.runner?.configs ?? [],
      defaultConfigId: parsed?.runner?.defaultConfigId ?? null,
      history: parsed?.runner?.history ?? [],
    };
  } catch {
    return defaults;
  }
}

export function setProjectRunnerSettings(
  currentJson: string | null,
  runner: Partial<ProjectRunnerSettings>,
): string {
  let current: Record<string, unknown> = {};
  if (currentJson) {
    try { current = JSON.parse(currentJson); } catch { /* use empty */ }
  }
  current.runner = { ...((current.runner as object) ?? {}), ...runner };
  return JSON.stringify(current);
}

const MAX_HISTORY = 10;

/** Add a command to the runner history (most recent first, deduped, capped). */
export function addToRunnerHistory(
  currentJson: string | null,
  entry: { name: string; command: string },
): string {
  const settings = getProjectRunnerSettings(currentJson);
  const now = Date.now();
  // Remove existing entry with same command, then prepend
  const filtered = settings.history.filter((h) => h.command !== entry.command);
  const updated = [{ ...entry, lastRun: now }, ...filtered].slice(0, MAX_HISTORY);
  return setProjectRunnerSettings(currentJson, { history: updated });
}
